import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  LayoutDashboard, CalendarCheck, Images, Clapperboard, Settings as SettingsIcon,
  LogOut, Check, X, Trash2, Plus, IndianRupee, Clock, Users, TrendingUp, Lock, Instagram, Gift,
  Contact, MessageCircle, Bell, BellRing, RotateCcw, Send, RefreshCw, CalendarDays,
} from "lucide-react";
import InstaReelsManager from "../components/InstaReelsManager";
import StaffManager from "../components/StaffManager";
import SchedulingPanel from "../components/SchedulingPanel";
import BookingsCalendar from "../components/BookingsCalendar";
import { waDigits, confirmMsg, waitingMsg, declineMsg, waCustomer, fmtTime12, fmtDate } from "../lib/bookingActions";
import {
  login, getToken, clearToken, getUsername, listBookings, patchBooking, removeBooking,
  listGallery, addGalleryUrl, addGalleryFile, removeGallery,
  listReels, addReelUrl, addReelFile, removeReel,
  getSettings, saveSettings, mediaSrc, listSpins, removeSpin, sendTestPush,
} from "../lib/api";
import { enablePush, pushSupported, pushPermission, isIOS, isStandalonePWA, syncPushIfGranted } from "../lib/push";
import { useToast } from "../hooks/use-toast";

const money = (n) => "₹" + (Number(n) || 0).toLocaleString("en-IN");

function NotificationBell() {
  const { toast } = useToast();
  const [perm, setPerm] = useState(pushPermission());
  const [busy, setBusy] = useState(false);
  const supported = pushSupported();

  const turnOn = async () => {
    if (perm === "granted") {
      try {
        await sendTestPush();
        toast({ title: "Test notification sent 🔔", description: "Check your screen / notification center!" });
      } catch {
        toast({ title: "Notifications active", description: "Your device is registered." });
      }
      return;
    }
    setBusy(true);
    try {
      await enablePush();
      setPerm("granted");
      toast({ title: "Notifications enabled 🔔", description: "You'll be alerted for every new booking." });
    } catch (e) {
      if (e.message === "ios_not_installed") {
        toast({
          title: "Install on iPhone First",
          description: "Tap Safari Share 📤 -> 'Add to Home Screen', open the app, then enable alerts.",
        });
      } else if (e.message === "denied") {
        toast({ title: "Notifications blocked", description: "Enable them in your browser/site settings." });
      } else if (e.message === "unsupported") {
        toast({ title: "Not supported in this browser", description: "On iPhone, add this app to your Home Screen first." });
      } else {
        toast({ title: "Could not enable notifications", description: "Please try again." });
      }
    } finally {
      setBusy(false);
    }
  };

  if (!supported) return null;
  const on = perm === "granted";
  return (
    <button
      onClick={turnOn}
      disabled={busy}
      data-testid="admin-notification-bell"
      title={on ? "Notifications on (tap to send test)" : "Enable notifications"}
      className="relative inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border hairline transition-colors"
      style={on ? { color: "var(--viimii-gold)", borderColor: "var(--viimii-gold)" } : { color: "var(--viimii-muted)" }}
    >
      {on ? <BellRing size={14} /> : <Bell size={14} />}
      <span className="hidden sm:inline">{busy ? "Enabling…" : on ? "Alerts on" : "Enable alerts"}</span>
    </button>
  );
}

function PushNotificationCard() {
  const { toast } = useToast();
  const [perm, setPerm] = useState(pushPermission());
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);
  const ios = isIOS();
  const pwa = isStandalonePWA();

  const handleEnable = async () => {
    setBusy(true);
    try {
      await enablePush();
      setPerm("granted");
      toast({
        title: "Push notifications active 🔔",
        description: "Your device is now registered to receive instant booking alerts.",
      });
    } catch (e) {
      if (e.message === "ios_not_installed") {
        toast({
          title: "Install on iPhone First",
          description: "Tap Safari Share 📤 -> 'Add to Home Screen', open the app from Home Screen, then tap Enable.",
        });
      } else if (e.message === "denied") {
        toast({
          title: "Permission Blocked",
          description: "Notifications are blocked in your browser. Enable them in site/device settings.",
        });
      } else {
        toast({
          title: "Could not enable push",
          description: "Please check your network connection and try again.",
        });
      }
    } finally {
      setBusy(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      await sendTestPush();
      toast({
        title: "Test notification sent 🚀",
        description: "Check your device's notification banner / lock screen right now!",
      });
    } catch (e) {
      toast({
        title: "Test notification failed",
        description: "Please ensure push alerts are enabled first.",
      });
    } finally {
      setTesting(false);
    }
  };

  const isGranted = perm === "granted";
  const isDenied = perm === "denied";

  return (
    <div
      data-testid="push-notifications-card"
      className="p-5 md:p-6 rounded-2xl border hairline bg-viimii-2 mb-8 relative overflow-hidden"
    >
      <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
        <div className="flex items-center gap-3">
          <div
            className="w-10 h-10 rounded-full grid place-items-center"
            style={{
              background: isGranted
                ? "rgba(127,201,154,0.18)"
                : "rgba(176,134,63,0.18)",
            }}
          >
            {isGranted ? (
              <BellRing size={20} style={{ color: "#3f9d63" }} />
            ) : (
              <Bell size={20} style={{ color: "var(--viimii-gold)" }} />
            )}
          </div>
          <div>
            <h3 className="font-display text-lg text-cream">
              Admin Push &amp; iOS Notifications
            </h3>
            <p className="text-xs text-cream/55">
              Receive instant alerts on your iPhone &amp; computer when a new booking arrives
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {isGranted ? (
            <span
              className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full font-medium"
              style={{ color: "#3f9d63", background: "rgba(127,201,154,0.18)" }}
            >
              <Check size={13} /> Active &amp; Subscribed
            </span>
          ) : isDenied ? (
            <span
              className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full font-medium"
              style={{ color: "#e08a8a", background: "rgba(224,138,138,0.18)" }}
            >
              <X size={13} /> Blocked in Settings
            </span>
          ) : (
            <span
              className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full font-medium"
              style={{ color: "var(--viimii-gold)", background: "rgba(176,134,63,0.18)" }}
            >
              <Clock size={13} /> Not Enabled Yet
            </span>
          )}
        </div>
      </div>

      {ios && !pwa && !isGranted && (
        <div
          data-testid="ios-install-guide"
          className="p-4 rounded-xl border hairline bg-viimii mb-4 text-xs space-y-2"
          style={{ borderColor: "rgba(176,134,63,0.4)" }}
        >
          <p className="font-medium text-cream flex items-center gap-1.5">
            📱 <span style={{ color: "var(--viimii-gold)" }}>iPhone / iOS Setup Steps:</span>
          </p>
          <ol className="list-decimal list-inside space-y-1 text-cream/75 leading-relaxed">
            <li>Open this page in <b>Safari</b> on your iPhone.</li>
            <li>Tap the <b>Share</b> button (square with arrow <span className="font-bold">📤</span> at the bottom of Safari).</li>
            <li>Scroll down and tap <b>"Add to Home Screen"</b>, then tap <b>Add</b>.</li>
            <li>Open the <b>VIIMII Admin</b> icon from your iPhone Home Screen.</li>
            <li>Tap <b>"Enable Push Alerts"</b> below and select <b>"Allow"</b>.</li>
          </ol>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3 pt-2">
        {!isGranted ? (
          <button
            onClick={handleEnable}
            disabled={busy}
            data-testid="enable-push-btn"
            className="inline-flex items-center gap-2 text-black text-sm font-medium px-5 py-2.5 rounded-xl hover:opacity-90 transition-opacity disabled:opacity-60"
            style={{ background: "var(--viimii-gold)" }}
          >
            <BellRing size={16} /> {busy ? "Enabling..." : "Enable Push Alerts"}
          </button>
        ) : (
          <button
            onClick={handleTest}
            disabled={testing}
            data-testid="send-test-push-btn"
            className="inline-flex items-center gap-2 text-black text-sm font-medium px-5 py-2.5 rounded-xl hover:opacity-90 transition-opacity disabled:opacity-60"
            style={{ background: "var(--viimii-gold)" }}
          >
            <Send size={15} /> {testing ? "Sending..." : "Send Test Notification"}
          </button>
        )}

        {isGranted && (
          <button
            onClick={handleEnable}
            disabled={busy}
            data-testid="resync-push-btn"
            className="inline-flex items-center gap-1.5 text-xs text-cream/60 hover:text-gold px-3 py-2 rounded-lg border hairline"
          >
            <RotateCcw size={13} /> Re-sync Device
          </button>
        )}
      </div>
    </div>
  );
}

function Login({ onLogin }) {
  const { toast } = useToast();
  const [u, setU] = useState("");
  const [p, setP] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(u, p);
      onLogin();
    } catch (err) {
      toast({ title: "Invalid credentials", description: "Please check username & password." });
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="min-h-screen grid place-items-center bg-viimii grain px-4" style={{ paddingTop: "env(safe-area-inset-top, 0px)" }}>
      <form onSubmit={submit} className="relative w-full max-w-sm bg-viimii-2 border hairline rounded-[28px] p-8">
        <div className="text-center mb-7">
          <div className="mx-auto w-14 h-14 rounded-full grid place-items-center mb-4" style={{ background: "rgba(201,161,90,0.15)" }}>
            <Lock size={22} className="text-gold" style={{ color: "var(--viimii-gold)" }} />
          </div>
          <img src="/brand/viimii-word-dark.png" alt="VIIMII STUDIO" className="h-7 w-auto mx-auto mb-2" />
          <p className="text-cream/50 text-sm mt-1">Studio management portal</p>
        </div>
        <div className="space-y-4">
          <input className="w-full bg-viimii border hairline rounded-xl px-4 py-3.5 text-cream placeholder-cream/35 focus:outline-none focus:border-gold" placeholder="Username" value={u} onChange={(e) => setU(e.target.value)} />
          <input type="password" className="w-full bg-viimii border hairline rounded-xl px-4 py-3.5 text-cream placeholder-cream/35 focus:outline-none focus:border-gold" placeholder="Password" value={p} onChange={(e) => setP(e.target.value)} />
          <button disabled={busy} className="w-full text-black font-medium py-3.5 rounded-xl hover:opacity-90 transition-opacity disabled:opacity-60" style={{ background: "var(--viimii-gold)" }}>{busy ? "Signing in..." : "Login"}</button>
        </div>
        <Link to="/" className="block text-center text-xs text-cream/40 hover:text-gold mt-6">← Back to website</Link>
        <p className="text-center text-[11px] text-cream/40 mt-5 pt-4 border-t hairline leading-relaxed">
          <span className="text-gold" style={{ color: "var(--viimii-gold)" }}>Install as an app:</span> on iPhone, tap the <b>Share</b> icon in Safari, then <b>“Add to Home Screen”</b> to open VIIMII Admin like a native app.
        </p>
      </form>
    </div>
  );
}

const STATUS_STYLES = {
  pending: { color: "var(--viimii-gold)", bg: "rgba(201,161,90,0.15)" },
  confirmed: { color: "#7fc99a", bg: "rgba(127,201,154,0.15)" },
  declined: { color: "#e08a8a", bg: "rgba(224,138,138,0.15)" },
  completed: { color: "var(--viimii-clay-soft)", bg: "rgba(216,161,132,0.15)" },
};

function Badge({ value }) {
  const s = STATUS_STYLES[value] || STATUS_STYLES.pending;
  return <span className="text-xs px-3 py-1 rounded-full capitalize" style={{ color: s.color, background: s.bg }}>{value}</span>;
}

function Bookings({ bookings, refresh, capacity }) {
  const { toast } = useToast();
  const confirmedCount = bookings.filter((b) => b.status === "confirmed").length;
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  const doRefresh = async () => {
    setRefreshing(true);
    try {
      await refresh();
      setLastUpdated(new Date());
      toast({ title: "Bookings refreshed" });
    } finally {
      setRefreshing(false);
    }
  };

  const act = async (id, patch, msg) => {
    try {
      await patchBooking(id, patch);
      await refresh();
      if (msg) toast({ title: msg });
      return true;
    } catch (err) {
      const detail = err?.response?.data?.detail || "Update failed";
      toast({ title: detail });
      return false;
    }
  };
  const del = async (id) => {
    await removeBooking(id);
    await refresh();
  };

  const acceptAndNotify = async (b) => {
    const ok = await act(b.id, { status: "confirmed" }, "Booking accepted");
    if (ok) window.open(waCustomer(b, confirmMsg(b)), "_blank");
  };
  const declineAndNotify = async (b) => {
    const ok = await act(b.id, { status: "declined" }, "Booking cancelled");
    if (ok) window.open(waCustomer(b, declineMsg(b)), "_blank");
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-3 flex-wrap">
          <h2 className="font-display text-2xl text-cream">Bookings</h2>
          <button
            onClick={doRefresh}
            disabled={refreshing}
            data-testid="bookings-refresh-btn"
            className="inline-flex items-center gap-2 text-sm px-4 py-2 rounded-full border hairline text-cream/75 hover:text-gold transition-colors disabled:opacity-60"
          >
            <RefreshCw size={15} className={refreshing ? "animate-spin" : ""} /> {refreshing ? "Refreshing…" : "Refresh"}
          </button>
        </div>
        <div className="flex items-center gap-2 text-sm px-4 py-2 rounded-full border hairline text-cream/75">
          <Users size={15} className="text-gold" style={{ color: "var(--viimii-gold)" }} />
          Slot capacity: <b className="text-cream">{confirmedCount}/{capacity}</b> confirmed
        </div>
      </div>
      {lastUpdated && (
        <p className="text-xs text-cream/40 -mt-4 mb-4" data-testid="bookings-last-updated">
          Last updated {lastUpdated.toLocaleTimeString()}
        </p>
      )}
      <div className="space-y-3">
        {bookings.length === 0 && <p className="text-cream/50">No bookings yet.</p>}
        {bookings.map((b) => (
          <div key={b.id} className="p-5 rounded-2xl border hairline bg-viimii-2">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-3 flex-wrap">
                  <h3 className="font-display text-lg text-cream">{b.name}</h3>
                  <Badge value={b.status} />
                  <span className="text-xs px-2.5 py-1 rounded-full" style={{ color: b.payment === "paid" ? "#7fc99a" : "#e08a8a", background: b.payment === "paid" ? "rgba(127,201,154,0.13)" : "rgba(224,138,138,0.13)" }}>{b.payment}</span>
                </div>
                <p className="text-sm text-cream/60 mt-1">{b.phone} · {b.service} · <span className="text-cream/45">{b.timing}</span></p>
                {b.date && <p className="text-sm mt-1 flex items-center gap-1.5" style={{ color: "var(--viimii-gold)" }}><CalendarCheck size={14} /> {fmtDate(b.date)}{b.time ? ` at ${fmtTime12(b.time)}` : ""}{b.duration ? ` · ${b.duration} min` : ""}</p>}
                {b.message && <p className="text-sm text-cream/45 mt-1 italic">"{b.message}"</p>}
                <p className="text-xs text-cream/35 mt-1">{new Date(b.createdAt).toLocaleString()}</p>
              </div>
              <div className="flex items-center gap-1.5 bg-viimii border hairline rounded-lg px-2 py-1">
                <IndianRupee size={13} className="text-gold" style={{ color: "var(--viimii-gold)" }} />
                <input type="number" defaultValue={b.amount} onBlur={(e) => act(b.id, { amount: Number(e.target.value) })} className="w-20 bg-transparent text-cream text-sm focus:outline-none" placeholder="Amount" />
              </div>
            </div>
            <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t hairline">
              {b.status !== "confirmed" && (
                <button onClick={() => acceptAndNotify(b)} data-testid="booking-accept-notify-btn" className="inline-flex items-center gap-1.5 text-sm px-4 py-2 rounded-lg text-black" style={{ background: "#7fc99a" }}>
                  <Check size={15} /> Accept &amp; Notify
                </button>
              )}
              {b.status !== "declined" && (
                <button onClick={() => declineAndNotify(b)} data-testid="booking-decline-notify-btn" className="inline-flex items-center gap-1.5 text-sm px-4 py-2 rounded-lg border hairline text-cream/80 hover:text-cream" style={{ borderColor: "rgba(224,138,138,0.4)" }}>
                  <X size={15} /> Decline &amp; Notify
                </button>
              )}
              <button onClick={() => act(b.id, { status: "completed" }, "Marked completed")} className="text-sm px-4 py-2 rounded-lg border hairline text-cream/70 hover:text-gold">Complete</button>
              <button onClick={() => act(b.id, { payment: b.payment === "paid" ? "unpaid" : "paid" })} className="text-sm px-4 py-2 rounded-lg border hairline text-cream/70 hover:text-gold">Mark {b.payment === "paid" ? "Unpaid" : "Paid"}</button>
              <a
                href={waCustomer(b, b.status === "confirmed" ? confirmMsg(b) : waitingMsg(b))}
                target="_blank"
                rel="noreferrer"
                data-testid="booking-whatsapp-btn"
                aria-label={`WhatsApp ${b.name}`}
                className="inline-flex items-center gap-1.5 text-sm px-4 py-2 rounded-lg text-white hover:opacity-90 transition-opacity"
                style={{ background: "#25D366" }}
              >
                <MessageCircle size={14} /> WhatsApp
              </a>
              <button onClick={() => del(b.id)} className="ml-auto text-sm px-3 py-2 rounded-lg text-cream/40 hover:text-red-400"><Trash2 size={15} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Dashboard({ bookings }) {
  const total = bookings.length;
  const pending = bookings.filter((b) => b.status === "pending").length;
  const confirmed = bookings.filter((b) => b.status === "confirmed").length;
  const revenue = bookings.filter((b) => b.payment === "paid").reduce((s, b) => s + (b.amount || 0), 0);
  const cards = [
    [CalendarCheck, "Total Bookings", total, "var(--viimii-gold)"],
    [Clock, "Pending", pending, "var(--viimii-clay-soft)"],
    [Check, "Confirmed", confirmed, "#7fc99a"],
    [TrendingUp, "Revenue (paid)", money(revenue), "var(--viimii-rose)"],
  ];
  return (
    <div>
      <h2 className="font-display text-2xl text-cream mb-6">Dashboard</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {cards.map(([Icon, label, val, c]) => (
          <div key={label} className="p-6 rounded-2xl border hairline bg-viimii-2">
            <span className="w-11 h-11 rounded-full grid place-items-center mb-4" style={{ background: "rgba(255,255,255,0.05)" }}>
              <Icon size={20} style={{ color: c }} />
            </span>
            <div className="font-display text-3xl text-cream">{val}</div>
            <div className="text-sm text-cream/50 mt-1">{label}</div>
          </div>
        ))}
      </div>
      <PushNotificationCard />
    </div>
  );
}

function MediaManager({ items, refresh, label, kind }) {
  const { toast } = useToast();
  const [url, setUrl] = useState("");
  const [caption, setCaption] = useState("");
  const [busy, setBusy] = useState(false);
  const isReel = kind === "reel";

  const onFile = async (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setBusy(true);
    try {
      if (isReel) await addReelFile(f, caption);
      else await addGalleryFile(f);
      setCaption("");
      await refresh();
      toast({ title: label + " uploaded" });
    } catch (err) {
      toast({ title: "Upload failed", description: err?.response?.data?.detail || "Try a smaller file." });
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };
  const addUrl = async () => {
    if (!url) return;
    setBusy(true);
    try {
      if (isReel) await addReelUrl(url, caption);
      else await addGalleryUrl(url);
      setUrl("");
      setCaption("");
      await refresh();
      toast({ title: label + " added" });
    } catch (err) {
      toast({ title: "Failed to add" });
    } finally {
      setBusy(false);
    }
  };
  const del = async (id) => {
    if (isReel) await removeReel(id);
    else await removeGallery(id);
    await refresh();
  };

  return (
    <div>
      <h2 className="font-display text-2xl text-cream mb-6">{label} Manager</h2>
      <div className="p-5 rounded-2xl border hairline bg-viimii-2 mb-6 space-y-3">
        {isReel && (
          <input value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="Caption (optional)" className="w-full bg-viimii border hairline rounded-xl px-4 py-3 text-cream placeholder-cream/35 focus:outline-none focus:border-gold" />
        )}
        <div className="flex flex-wrap gap-3">
          <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="Paste image / video URL" className="flex-1 min-w-[220px] bg-viimii border hairline rounded-xl px-4 py-3 text-cream placeholder-cream/35 focus:outline-none focus:border-gold" />
          <button disabled={busy} onClick={addUrl} className="inline-flex items-center gap-1.5 text-black font-medium px-5 py-3 rounded-xl disabled:opacity-60" style={{ background: "var(--viimii-gold)" }}><Plus size={16} /> Add</button>
          <label className="inline-flex items-center gap-1.5 px-5 py-3 rounded-xl border hairline text-cream/80 hover:text-gold cursor-pointer">
            {busy ? "Uploading..." : "Upload"}
            <input type="file" accept={isReel ? "video/*,image/*" : "image/*"} className="hidden" onChange={onFile} />
          </label>
        </div>
        <p className="text-xs text-cream/40">{isReel ? "Upload your reel videos (mp4/webm) - they autoplay muted on loop in the hero." : "Upload your nail & trend photos to the portfolio gallery."} Files are stored securely in the cloud.</p>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
        {items.map((it) => (
          <div key={it.id} className="relative group rounded-2xl overflow-hidden border hairline bg-black">
            {it.type === "video" ? (
              <video src={mediaSrc(it.src)} className="w-full aspect-[3/4] object-cover" muted loop autoPlay playsInline />
            ) : (
              <img src={mediaSrc(it.src)} alt={it.caption || "media"} className="w-full aspect-[3/4] object-cover" />
            )}
            {it.caption && <span className="absolute bottom-2 left-2 text-xs text-white/90">{it.caption}</span>}
            <button onClick={() => del(it.id)} className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/60 grid place-items-center text-white opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 size={14} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function SettingsPanel({ settings, save }) {
  const { toast } = useToast();
  const [cap, setCap] = useState(settings.capacity);
  const [slot, setSlot] = useState(settings.slotMinutes);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setCap(settings.capacity); setSlot(settings.slotMinutes); }, [settings]);
  return (
    <div className="max-w-lg">
      <h2 className="font-display text-2xl text-cream mb-6">Studio Settings</h2>
      <div className="p-6 rounded-2xl border hairline bg-viimii-2 space-y-5">
        <div>
          <label className="text-sm text-cream/70 flex items-center gap-2 mb-2"><Users size={15} className="text-gold" style={{ color: "var(--viimii-gold)" }} /> Max clients per time slot</label>
          <input type="number" min={1} value={cap} onChange={(e) => setCap(Number(e.target.value))} className="w-full bg-viimii border hairline rounded-xl px-4 py-3 text-cream focus:outline-none focus:border-gold" />
          <p className="text-xs text-cream/40 mt-1">Bookings can't be accepted beyond this number per slot.</p>
        </div>
        <div>
          <label className="text-sm text-cream/70 flex items-center gap-2 mb-2"><Clock size={15} className="text-gold" style={{ color: "var(--viimii-gold)" }} /> Slot length (minutes)</label>
          <input type="number" min={15} step={15} value={slot} onChange={(e) => setSlot(Number(e.target.value))} className="w-full bg-viimii border hairline rounded-xl px-4 py-3 text-cream focus:outline-none focus:border-gold" />
        </div>
        <button onClick={async () => { await save({ capacity: cap, slotMinutes: slot }); toast({ title: "Settings saved" }); }} className="text-black font-medium px-6 py-3 rounded-xl" style={{ background: "var(--viimii-gold)" }}>Save Settings</button>
      </div>
      <div className="mt-8">
        <PushNotificationCard />
      </div>
    </div>
  );
}

function SpinsPanel() {
  const { toast } = useToast();
  const [spins, setSpins] = useState([]);
  const refresh = async () => {
    try {
      setSpins(await listSpins());
    } catch {
      toast({ title: "Could not load spin entries", description: "Please re-open the tab or log in again." });
    }
  };
  useEffect(() => { refresh(); }, []);
  const del = async (id) => {
    await removeSpin(id);
    await refresh();
    toast({ title: "Entry removed" });
  };
  return (
    <div data-testid="spins-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h2 className="font-display text-2xl text-cream">Spin &amp; Win Entries</h2>
        <div className="flex items-center gap-2 text-sm px-4 py-2 rounded-full border hairline text-cream/75">
          <Gift size={15} style={{ color: "var(--viimii-gold)" }} />
          <b className="text-cream">{spins.length}</b> spins
        </div>
      </div>
      <div className="space-y-3">
        {spins.length === 0 && <p className="text-cream/50">No spins yet.</p>}
        {spins.map((s) => (
          <div key={s.id} data-testid="spin-entry-row" className="p-5 rounded-2xl border hairline bg-viimii-2 flex flex-wrap items-center justify-between gap-4">
            <div>
              <h3 className="font-display text-lg text-cream">{s.name}</h3>
              <p className="text-sm text-cream/60 mt-0.5">{s.phone}</p>
              <p className="text-sm mt-1" style={{ color: "var(--viimii-gold)" }}>{s.prize}</p>
              <p className="text-xs text-cream/35 mt-1">{new Date(s.createdAt).toLocaleString()}</p>
            </div>
            <button data-testid="spin-entry-delete" onClick={() => del(s.id)} className="text-cream/50 hover:text-red-400 p-2" aria-label="Delete entry">
              <Trash2 size={17} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

const BULK_TEMPLATES = [
  { label: "Anniversary", text: "Hi {name}! 🎉 VIIMII STUDIO is celebrating our anniversary and we'd love to pamper you! Book your slot and enjoy something special on us." },
  { label: "Festive Offer", text: "Hi {name}! ✨ Festive season is here at VIIMII STUDIO - exclusive offers on nails, lashes & spa. Reply to grab your slot before they fill up!" },
  { label: "Rebooking Reminder", text: "Hi {name}! It's been a while since your last visit to VIIMII STUDIO. Ready for a fresh new look? Reply here and we'll set up your appointment." },
  { label: "Claim Prize", text: "Hi {name}! You won \"{prize}\" on our Anniversary Spin & Win at VIIMII STUDIO. Reply here to claim it - we can't wait to see you!" },
];

function ContactsPanel() {
  const { toast } = useToast();
  const [spins, setSpins] = useState([]);
  const [selected, setSelected] = useState(() => new Set());
  const [bulk, setBulk] = useState(null); // { idx, message } | null
  useEffect(() => {
    listSpins().then(setSpins).catch(() => toast({ title: "Could not load contacts", description: "Please re-open the tab or log in again." }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const seen = new Set();
  const contacts = spins.filter((s) => {
    const key = waDigits(s.phone) || s.phone;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  const keyOf = (c) => waDigits(c.phone) || c.phone;
  const allSelected = contacts.length > 0 && contacts.every((c) => selected.has(keyOf(c)));
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(contacts.map(keyOf)));
  const toggleOne = (c) => {
    const k = keyOf(c);
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  };
  const chosen = contacts.filter((c) => selected.has(keyOf(c)));
  const startBulk = () =>
    setBulk({
      idx: 0,
      message: "Hi {name}! This is VIIMII STUDIO. We're celebrating our anniversary - a little treat is waiting for you at the studio. Reply here to book your slot!",
    });
  const sendCurrent = () => {
    const c = chosen[bulk.idx];
    const msg = bulk.message.replaceAll("{name}", c.name).replaceAll("{prize}", c.prize);
    window.open(`https://wa.me/${keyOf(c)}?text=${encodeURIComponent(msg)}`, "_blank");
    if (bulk.idx + 1 < chosen.length) {
      setBulk((b) => ({ ...b, idx: b.idx + 1 }));
    } else {
      setBulk(null);
      setSelected(new Set());
      toast({ title: "Bulk WhatsApp complete", description: `Opened chats for ${chosen.length} contact(s).` });
    }
  };
  return (
    <div data-testid="contacts-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h2 className="font-display text-2xl text-cream">Contacts</h2>
        <div className="flex items-center gap-2 text-sm px-4 py-2 rounded-full border hairline text-cream/75">
          <Contact size={15} style={{ color: "var(--viimii-gold)" }} />
          <b className="text-cream">{contacts.length}</b> contacts
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-4 mb-4">
        <label className="flex items-center gap-2 text-sm text-cream/70 cursor-pointer" data-testid="contact-select-all">
          <input type="checkbox" checked={allSelected} onChange={toggleAll} className="w-4 h-4 accent-[#b0863f]" /> Select all
        </label>
        <button
          onClick={startBulk}
          disabled={chosen.length === 0}
          data-testid="bulk-whatsapp-btn"
          className="inline-flex items-center gap-2 text-white text-sm font-medium px-4 py-2 rounded-full hover:opacity-90 transition-opacity disabled:opacity-40"
          style={{ background: "#25D366" }}
        >
          <MessageCircle size={15} /> Bulk WhatsApp ({chosen.length})
        </button>
      </div>
      <div className="space-y-3">
        {contacts.length === 0 && <p className="text-cream/50">No contacts yet - they appear here when someone spins the wheel.</p>}
        {contacts.map((c) => (
          <div key={c.id} data-testid="contact-row" className="p-5 rounded-2xl border hairline bg-viimii-2 flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                data-testid="contact-select"
                checked={selected.has(keyOf(c))}
                onChange={() => toggleOne(c)}
                className="w-4 h-4 mt-1.5 shrink-0 accent-[#b0863f]"
                aria-label={`Select ${c.name}`}
              />
              <div>
                <h3 className="font-display text-lg text-cream">{c.name}</h3>
                <p className="text-sm text-cream/60 mt-0.5">{c.phone}</p>
                <p className="text-sm mt-1" style={{ color: "var(--viimii-gold)" }}>{c.prize}</p>
                <p className="text-xs text-cream/35 mt-1">{new Date(c.createdAt).toLocaleString()}</p>
              </div>
            </div>
            <a
              href={`https://wa.me/${waDigits(c.phone)}?text=${encodeURIComponent(
                c.prize === "Better Luck Next Time"
                  ? `Hi ${c.name}! This is VIIMII STUDIO. Thank you for playing our Anniversary Spin & Win! We'd love to welcome you with a special treat on your next visit.`
                  : `Hi ${c.name}! This is VIIMII STUDIO. You won "${c.prize}" on our Anniversary Spin & Win - we'd love to help you claim it!`
              )}`}
              target="_blank"
              rel="noreferrer"
              data-testid="contact-whatsapp-btn"
              aria-label={`WhatsApp ${c.name}`}
              className="inline-flex items-center gap-2 text-white text-sm font-medium px-4 py-2.5 rounded-full hover:opacity-90 transition-opacity"
              style={{ background: "#25D366" }}
            >
              <MessageCircle size={15} /> WhatsApp
            </a>
          </div>
        ))}
      </div>

      {bulk && chosen.length > 0 && (
        <div className="fixed inset-0 z-[70] grid place-items-center p-4" data-testid="bulk-whatsapp-modal">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setBulk(null)} />
          <div className="relative w-full max-w-md bg-viimii-2 border hairline rounded-[28px] p-7">
            <button onClick={() => setBulk(null)} data-testid="bulk-close-btn" className="absolute top-5 right-5 text-cream/60 hover:text-cream" aria-label="Close">
              <X size={20} />
            </button>
            <p className="section-line text-gold mb-2">Bulk WhatsApp</p>
            <h3 className="font-display text-2xl text-cream mb-4">Message {chosen.length} contact{chosen.length > 1 ? "s" : ""}</h3>
            <p className="text-xs text-cream/50 mb-2">Templates</p>
            <div className="flex flex-wrap gap-2 mb-4" data-testid="bulk-templates">
              {BULK_TEMPLATES.map((t) => (
                <button
                  key={t.label}
                  type="button"
                  data-testid={`bulk-template-${t.label.toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={() => setBulk((b) => ({ ...b, message: t.text }))}
                  className="text-xs px-3 py-1.5 rounded-full border hairline text-cream/70 hover:text-black transition-colors"
                  style={bulk.message === t.text ? { background: "var(--viimii-gold)", color: "#000" } : {}}
                >
                  {t.label}
                </button>
              ))}
            </div>
            <textarea
              rows={4}
              value={bulk.message}
              onChange={(e) => setBulk((b) => ({ ...b, message: e.target.value }))}
              data-testid="bulk-message-input"
              className="w-full bg-viimii border hairline rounded-xl px-4 py-3 text-cream text-sm focus:outline-none focus:border-gold"
            />
            <p className="text-[11px] text-cream/40 mt-1 mb-5">Use {"{name}"} and {"{prize}"} - they're replaced for each contact.</p>
            <div className="p-4 rounded-xl bg-viimii border hairline mb-4">
              <p className="text-xs text-cream/45">Now sending to</p>
              <p className="text-cream font-medium" data-testid="bulk-current-contact">
                {chosen[bulk.idx].name} · {chosen[bulk.idx].phone}
              </p>
              <p className="text-xs text-cream/45 mt-1">Contact {bulk.idx + 1} of {chosen.length}</p>
            </div>
            <button
              onClick={sendCurrent}
              data-testid="bulk-send-btn"
              className="w-full inline-flex items-center justify-center gap-2 text-white font-medium py-3.5 rounded-xl hover:opacity-90 transition-opacity"
              style={{ background: "#25D366" }}
            >
              <MessageCircle size={16} /> Open WhatsApp for {chosen[bulk.idx].name} ({bulk.idx + 1}/{chosen.length})
            </button>
            <p className="text-[11px] text-cream/40 mt-3 text-center">Each tap opens one chat - hit send in WhatsApp, then come back for the next contact.</p>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Admin() {
  const { toast } = useToast();
  const [authed, setAuthed] = useState(!!getToken());
  const [tab, setTab] = useState("dashboard");
  const [bookings, setBookings] = useState([]);
  const [gallery, setGalleryList] = useState([]);
  const [reels, setReelsList] = useState([]);
  const [settings, setSettingsState] = useState({ capacity: 3, slotMinutes: 60 });

  const prevBookingsCountRef = React.useRef(0);

  const refreshBookings = async () => {
    try {
      const data = await listBookings();
      setBookings(data);
      prevBookingsCountRef.current = data.length;
    } catch {
      clearToken();
      setAuthed(false);
    }
  };
  const refreshGallery = async () => setGalleryList(await listGallery());
  const refreshReels = async () => setReelsList(await listReels());
  const refreshSettings = async () => setSettingsState(await getSettings());

  useEffect(() => {
    if (!authed) return;
    syncPushIfGranted().catch(() => {});
    refreshBookings();
    refreshGallery();
    refreshReels();
    refreshSettings();

    // Auto-polling for real-time audible & visual alerts while Admin is open
    const poller = setInterval(async () => {
      try {
        const fresh = await listBookings();
        if (prevBookingsCountRef.current > 0 && fresh.length > prevBookingsCountRef.current) {
          const newest = fresh[0];
          try {
            const audio = new Audio("https://assets.mixkit.co/active_storage/sfx/2869/2869-preview.mp3");
            audio.volume = 0.6;
            audio.play().catch(() => {});
          } catch {}
          toast({
            title: "New Booking Received! 🔔",
            description: `${newest.name} booked ${newest.service || "a service"}.`,
          });
          setBookings(fresh);
        }
        prevBookingsCountRef.current = fresh.length;
      } catch {}
    }, 12000);

    return () => clearInterval(poller);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed]);

  // Refresh bookings whenever the app is brought back to the foreground
  useEffect(() => {
    if (!authed) return;
    const onForeground = () => {
      if (document.visibilityState === "visible") refreshBookings();
    };
    document.addEventListener("visibilitychange", onForeground);
    window.addEventListener("focus", onForeground);
    return () => {
      document.removeEventListener("visibilitychange", onForeground);
      window.removeEventListener("focus", onForeground);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed]);

  if (!authed) return <Login onLogin={() => setAuthed(true)} />;

  const NAV = [
    ["dashboard", "Dashboard", LayoutDashboard],
    ["bookings", "Bookings", CalendarCheck],
    ["calendar", "Calendar", CalendarDays],
    ["spins", "Spin & Win", Gift],
    ["contacts", "Contacts", Contact],
    ["scheduling", "Scheduling", Clock],
    ["staff", "Staff & Salary", Users],
    ["heroreels", "Hero Reels", Instagram],
    ["gallery", "Designs & Trends", Images],
    ["reels", "Reels", Clapperboard],
    ["settings", "Settings", SettingsIcon],
  ];

  const doSaveSettings = async (s) => { await saveSettings(s); await refreshSettings(); };

  return (
    <div className="min-h-screen bg-viimii grain">
      {/* Top header with logo - respects iPhone safe area (Dynamic Island / notch) */}
      <header
        className="sticky top-0 z-50 bg-viimii-2/95 backdrop-blur border-b hairline flex items-center justify-between px-5 md:px-6"
        style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 0.65rem)", paddingBottom: "0.65rem" }}
      >
        <div className="flex items-center gap-3">
          <img src="/brand/viimii-word-dark.png" alt="VIIMII STUDIO" className="h-6 w-auto" />
          <span className="hidden sm:inline text-[11px] tracking-[0.2em] uppercase" style={{ color: "var(--viimii-clay)" }}>Admin</span>
        </div>
        <div className="flex items-center gap-4">
          <NotificationBell />
          <Link to="/" className="text-xs text-cream/55 hover:text-gold">View website</Link>
          <button onClick={() => { clearToken(); setAuthed(false); }} className="inline-flex items-center gap-1.5 text-xs text-cream/60 hover:text-red-400">
            <LogOut size={15} /> Logout
          </button>
        </div>
      </header>

      <div className="flex flex-col md:flex-row">
        <aside
          className="md:w-64 md:min-h-[calc(100vh-56px)] border-b md:border-b-0 md:border-r hairline bg-viimii-2 p-4 md:p-5 flex md:flex-col gap-2 md:gap-1 overflow-x-auto md:sticky md:top-[56px] md:self-start"
          style={{ paddingLeft: "max(1rem, env(safe-area-inset-left))", paddingRight: "max(1rem, env(safe-area-inset-right))" }}
        >
          {NAV.map(([k, label, Icon]) => (
            <button key={k} onClick={() => setTab(k)} className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm whitespace-nowrap transition-colors ${tab === k ? "text-black" : "text-cream/70 hover:text-gold"}`} style={tab === k ? { background: "var(--viimii-gold)" } : {}}>
              <Icon size={17} /> {label}
            </button>
          ))}
        </aside>

        <main
          className="flex-1 p-5 md:p-10"
          style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 2rem)" }}
        >
          <div className="flex items-center justify-between mb-8">
            <h1 className="font-display text-xl text-cream capitalize">{NAV.find((n) => n[0] === tab)?.[1]}</h1>
            <span className="text-sm text-cream/50">Welcome back, {getUsername()}</span>
          </div>
          {tab === "dashboard" && <Dashboard bookings={bookings} />}
          {tab === "bookings" && <Bookings bookings={bookings} refresh={refreshBookings} capacity={settings.capacity} />}
          {tab === "calendar" && <BookingsCalendar bookings={bookings} refresh={refreshBookings} />}
          {tab === "spins" && <SpinsPanel />}
          {tab === "contacts" && <ContactsPanel />}
          {tab === "scheduling" && <SchedulingPanel />}
          {tab === "staff" && <StaffManager />}
          {tab === "heroreels" && <InstaReelsManager />}
          {tab === "gallery" && <MediaManager items={gallery} refresh={refreshGallery} label="Designs & Trends" kind="gallery" />}
          {tab === "reels" && <MediaManager items={reels} refresh={refreshReels} label="Reels" kind="reel" />}
          {tab === "settings" && <SettingsPanel settings={settings} save={doSaveSettings} />}
        </main>
      </div>
    </div>
  );
}
